/**
 * Resolving a Claude Code reset instant from the human string the CLI renders.
 *
 * ENG-9394 extracted this from `banner-parser.ts` unchanged, because a SECOND
 * reader of the same strings now exists: `usage-report-parser.ts`, which parses
 * the `claude -p "/usage"` report rather than a scraped pane banner.
 *
 * The extraction is deliberate and is the point of the file. Reset strings carry
 * no year, so resolving one is a heuristic, and the specific heuristic here is
 * load-bearing history:
 *
 *   - ENG-6416 / ENG-6379 / ENG-6415: a year rule with a fixed past-window has a
 *     discontinuity at `now - N`, so the same banner text flips year across the
 *     reset boundary and pins downstream alerts open for ~12 months. Nearest
 *     occurrence among prev/current/next year has no such boundary.
 *   - ENG-8472: a date-less reset reconstructed as "next occurrence of that hour"
 *     lands every host that names the same hour on the same millisecond, which
 *     was read for 11 days as evidence of a shared subscription. Hence
 *     `ResetPrecision`, so a caller can tell a reading from a reconstruction.
 *
 * A second copy of that reasoning would drift from this one, and the drift would
 * look exactly like ENG-8472 again. There is one implementation and both parsers
 * call it.
 */

/**
 * How a reset instant was obtained (ENG-8901).
 *
 * `dated` - the source named a date, so the instant is READ.
 *
 * `hour_only` - the source named ONLY an hour ("resets 1am (UTC)", the ENG-7904
 * weekly banner form) and the day was RECONSTRUCTED as the next occurrence of
 * that hour. The value is therefore at most 24 hours in the future and can be
 * days earlier than the real weekly reset. Do not render it as a measurement.
 */
export type ResetPrecision = 'dated' | 'hour_only';

export interface ResolvedReset {
  at: Date;
  precision: ResetPrecision;
}

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

const FULL_MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

/**
 * The COMPLETE month tokens we accept, lower-cased, mapped to a 0-based index.
 *
 * Complete, not a prefix (CodeRabbit on #4988). The earlier implementation did
 * `token.slice(0, 3)`, which reads the first three letters of ANYTHING: the
 * grammar in `RESET_DATE_SOURCE` accepts `[A-Za-z]{3,9}`, so "Juniper 25" and
 * "Marzipan 3" both satisfied it and both resolved to a confident, wrong,
 * `dated` instant. A reset boundary invented out of a word that is not a month
 * is worse than no boundary at all - "unknown" gates nothing, whereas a wrong
 * instant produces an alert that closes on the wrong day.
 *
 * The token set is deliberately a set of WHOLE words rather than a tighter
 * regex, because more than one rendering is reachable and all of them must keep
 * working: the same grammar admits "Sep", "Sept" and "September". An earlier
 * draft of this fix rejected the finding outright on exactly that ground - but
 * enumerating the real variants keeps every reachable render AND rejects
 * "Juniper", so the choice was never actually between the two.
 */
const MONTH_TOKENS: ReadonlyMap<string, number> = new Map<string, number>([
  ...MONTHS.map((m, i) => [m, i] as [string, number]),
  ...FULL_MONTHS.map((m, i) => [m, i] as [string, number]),
  // "Sept" is the one four-letter abbreviation in common use and is inside the
  // {3,9} the grammar accepts, so it is reachable and must resolve.
  ['sept', 8],
]);

/**
 * A time-of-day like "5pm", "5:30pm", "1am (UTC)". No capturing groups, so it can
 * be embedded in a larger pattern without shifting the outer capture indices.
 */
export const TIME_OF_DAY_SOURCE = /\d{1,2}(?::\d{2})?\s*(?:am|pm)(?:\s*\(?UTC\)?)?/
  .source;

/**
 * A reset target as rendered, either "Mon DD" with an optional ", <time>" tail,
 * or a bare date-less "<time>". Parsed by `parseResetDateTime`, which tolerates
 * all three shapes. Dated form first so "May 26, 5pm" takes the fuller branch.
 */
export const RESET_DATE_SOURCE = `(?:[A-Za-z]{3,9}\\s+\\d{1,2}(?:\\s*,\\s*${TIME_OF_DAY_SOURCE})?|${TIME_OF_DAY_SOURCE})`;

// Matches the optional ", H[:MM]am/pm (UTC)" tail on a dated reset string.
// Hour is group 1, minutes (optional) group 2, am/pm group 3.
const TIME_TAIL = /,\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)(?:\s*\(?UTC\)?)?\s*$/i;

// Matches a bare, date-less reset time (the ENG-7904 weekly saturated banner),
// e.g. "1am (UTC)", "1:30am", "12pm". Same capture shape as TIME_TAIL.
const TIME_ONLY = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)(?:\s*\(?UTC\)?)?$/i;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Convert a captured 12-hour clock time to 24-hour {hour, minute}, or null if
 * out of range. 12am -> 00:xx, 12pm -> 12:xx, 1pm -> 13:xx.
 */
function parseAmPm(
  hourStr: string,
  minStr: string | undefined,
  ampm: string,
): { hour: number; minute: number } | null {
  const rawHour = Number.parseInt(hourStr, 10);
  if (!Number.isFinite(rawHour) || rawHour < 1 || rawHour > 12) return null;
  let minute = 0;
  if (minStr) {
    minute = Number.parseInt(minStr, 10);
    if (!Number.isFinite(minute) || minute < 0 || minute > 59) return null;
  }
  const isPm = ampm.toLowerCase() === 'pm';
  return { hour: (rawHour % 12) + (isPm ? 12 : 0), minute };
}

/**
 * Resolve a rendered reset string ("Aug 25, 12:59am (UTC)", "May 26", "1am
 * (UTC)") to an instant, plus whether that instant was read or reconstructed.
 *
 * Returns null when the string is not a shape we recognise. A null here must
 * become "unknown" at the call site and must never be collapsed to "at limit" -
 * failing closed on a usage reading starves the fleet, which is worse than the
 * alert it would raise (ENG-9394).
 */
export function parseResetDateTime(humanDate: string, now: Date): ResolvedReset | null {
  const trimmed = humanDate.trim();

  // Date-less reset (ENG-7904): the weekly saturated banner renders only the
  // reset hour ("resets 1am (UTC)"). Resolve to the NEXT occurrence of that UTC
  // time relative to `now`.
  //
  // ENG-8901: this is a RECONSTRUCTION and the caller is told so via
  // `precision`. A weekly reset can be seven days out and this branch can never
  // produce an instant more than 24 hours away, so the value is a lower bound on
  // the real boundary, not a measurement of it.
  const timeOnly = trimmed.match(TIME_ONLY);
  if (timeOnly) {
    const hm = parseAmPm(timeOnly[1]!, timeOnly[2], timeOnly[3]!);
    if (!hm) return null;
    const todayAt = Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      hm.hour,
      hm.minute,
    );
    return {
      at: new Date(todayAt <= now.getTime() ? todayAt + MS_PER_DAY : todayAt),
      precision: 'hour_only',
    };
  }

  // Split optional time tail from the leading "Mon DD" portion.
  const timeMatch = trimmed.match(TIME_TAIL);
  const dateOnly = timeMatch ? trimmed.slice(0, timeMatch.index).trim() : trimmed;

  const parts = dateOnly.split(/\s+/);
  if (parts.length !== 2) return null;

  // The WHOLE token must be a month we know. See MONTH_TOKENS - a prefix match
  // turned any 3-9 letter word into a month and therefore into a confident
  // wrong reset instant.
  const lookedUpMonth = MONTH_TOKENS.get(parts[0]!.toLowerCase());
  if (lookedUpMonth === undefined) return null;
  // Re-bound with an explicit `number` rather than used narrowed. The candidate
  // loop below now compares `candidate.getUTCMonth()` against this value, so
  // leaving it as a narrowed `number | undefined` makes the loop's control flow
  // depend on `candidate` while `candidate`'s own type depends on the narrowing
  // - a genuine circularity that tsc reports as TS7022 on `candidate`.
  const month: number = lookedUpMonth;

  const day = Number.parseInt(parts[1]!, 10);
  if (!Number.isFinite(day) || day < 1 || day > 31) return null;

  let hour = 0;
  let minute = 0;
  if (timeMatch) {
    const hm = parseAmPm(timeMatch[1]!, timeMatch[2], timeMatch[3]!);
    if (!hm) return null;
    hour = hm.hour;
    minute = hm.minute;
  }

  // Reset strings carry no year, so we must infer it. A Claude Code reset is
  // always within a few days of `now` in EITHER direction: right after one
  // elapses the source can still hold the just-passed string (a recent PAST
  // date) before it re-renders the next window (a near FUTURE date). The reset
  // must therefore resolve to the SAME instant whether read just before or just
  // after the boundary (ENG-6416) - a year that flips with read time pins
  // downstream alerts open for ~12 months (the ENG-6379 / ENG-6415 symptom).
  //
  // Earlier heuristics used "this year unless the candidate is more than N days
  // in the past, then roll +1" (N=1, later widened to 6 on ENG-6379). Any fixed
  // past-window still has a hard discontinuity at `now - N`: the same text flips
  // 2026<->2027 the moment `now` crosses it.
  //
  // Instead, resolve to the occurrence of (month, day, time) NEAREST to `now`
  // among the previous / current / next UTC year. Annual occurrences are ~365
  // days apart, so for any realistic +/-7-day reset exactly one candidate is
  // close and "nearest" is unambiguous and deterministic. The only read time at
  // which the chosen year flips is the ~6-month antipode of the reset date - a
  // point at which no reset for that date is ever rendered - so the boundary
  // oscillation is eliminated for every real input. This also covers the
  // Dec<->Jan wrap in both directions for free.
  const baseYear = now.getUTCFullYear();
  let resolved: Date | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const y of [baseYear - 1, baseYear, baseYear + 1]) {
    const candidate = new Date(Date.UTC(y, month, day, hour, minute));
    // `Date.UTC` SILENTLY ROLLS OVER an impossible date: Feb 31 becomes Mar 3.
    // The grammar accepts any day 1-31 for any month, so "resets Feb 31" parses
    // and would resolve to a real-looking instant three days from where it
    // claimed to be (CodeRabbit on #4988). Reject it instead, so it degrades to
    // unknown - which gates nothing - rather than to a confident wrong boundary.
    //
    // The check round-trips YEAR, MONTH and DAY (CodeRabbit on #4988), and the
    // DAY arm is the one that currently fires: "Feb 31" is the live case.
    //
    // Be honest about the other two. Given the guards above - month is a
    // validated index, day is already range-checked to 1-31, and parseAmPm
    // range-checks the time - the ONLY component that can roll today is the day,
    // and any roll necessarily moves getUTCDate. So the year and month arms are
    // unreachable as the code stands and cannot be given a failing test; they
    // are kept because they cost nothing and they stop being unreachable the
    // moment someone widens the day guard (a "Dec 32" that got past it rolls the
    // month and the year too). A narrow check that is only correct because of an
    // argument made three checks earlier is how the next edit reintroduces this.
    if (
      candidate.getUTCFullYear() !== y ||
      candidate.getUTCMonth() !== month ||
      candidate.getUTCDate() !== day
    ) {
      continue;
    }
    const delta = Math.abs(candidate.getTime() - now.getTime());
    if (delta < bestDelta) {
      bestDelta = delta;
      resolved = candidate;
    }
  }
  // The month and day came from the source; only the YEAR is inferred, and it is
  // pinned by nearest-occurrence. That is a reading, not a reconstruction.
  return resolved ? { at: resolved, precision: 'dated' } : null;
}
